import { Center, Text } from '@chakra-ui/react';
import NimioPlayer from '@components/common/video/NimioPlayer';
import ShakaPlayer from '@components/common/video/ShakaPlayer';
import { memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { PlayerTypeEnum } from '@/types/commons/commons.types';

import HlsPlayer from './HlsPlayer';
import OvenMediaPlayer from './OvenMediaPlayer';
import SLDPPlayer from './SLDPPlayer';

export interface PlayerProps {
  src: string | null;
  id: string;
  displayMessageCentered?: boolean;
  isPaused?: boolean;
  generatedByNimble?: boolean;
  playerType?: PlayerTypeEnum;
}

const ErrorMessage = ({ centered }: { centered: boolean }) => (
  <Center width="100%" height="100%" backgroundColor="black">
    <Text transform={centered ? undefined : 'translateY(100px)'}>
      <FormattedMessage id="cameras.playerCrash" />
    </Text>
  </Center>
);

type InternalPlayerProps = Omit<PlayerProps, 'src'> & { src: string };

type PlayerComponentType = React.ComponentType<InternalPlayerProps>;

const PLAYER_COMPONENTS: Record<PlayerTypeEnum, PlayerComponentType> = {
  [PlayerTypeEnum.OMP]: OvenMediaPlayer,
  [PlayerTypeEnum.HLS_JS]: HlsPlayer,
  [PlayerTypeEnum.NIMIO]: NimioPlayer,
  [PlayerTypeEnum.SHAKA]: ShakaPlayer,
  [PlayerTypeEnum.SLDP]: SLDPPlayer,
};

function Player({ displayMessageCentered = true, ...props }: Readonly<PlayerProps>) {
  const className = displayMessageCentered ? undefined : 'lower-message';

  if (props.src === null) {
    return <ErrorMessage centered={displayMessageCentered} />;
  }
  const playerProps = { ...props, src: props.src, className };

  const PlayerComponent = PLAYER_COMPONENTS[props.playerType ?? PlayerTypeEnum.SLDP];

  return (
    <ErrorBoundary fallback={<ErrorMessage centered={displayMessageCentered} />} resetKeys={[props.src]}>
      <PlayerComponent {...playerProps} />
    </ErrorBoundary>
  );
}

export default memo(Player);
